<script lang="ts">
	import type { MarkdownOptions, Renderers } from '@magidoc/plugin-svelte-marked';
	import type { Tokens } from 'marked';
	import { twMerge } from 'tailwind-merge';
	import { Link } from '../../../Link';
	import { Txt, type TxtVariant } from '../../../Txt';
	import { getMarkdownContext } from '../../utils/getMarkdownContext/getMarkdownContext';
	import { getMarkdownSafetyContext } from '../../utils/getMarkdownSafetyContext/getMarkdownSafetyContext';
	import { isSafeMarkdownUrl } from '../../utils/isSafeMarkdownUrl/isSafeMarkdownUrl';
	import { MarkdownInlineTokens } from '../MarkdownInlineTokens';

	interface Props {
		variant: TxtVariant;
		token: Tokens.Link;
		class?: string | undefined;
		options: MarkdownOptions | undefined;
		renderers: Renderers | undefined;
	}

	let { options, renderers, ...props }: Props = $props();

	const markdownContext = getMarkdownContext();
	const domain = markdownContext?.domain;
	const safety = getMarkdownSafetyContext();

	let href = $derived(
		domain && props.token.href.startsWith(domain)
			? props.token.href.split(domain)[1]
			: props.token.href
	);

	// Untrusted Markdown keeps the link text but drops a href that isn't
	// http(s)/mailto (e.g. `javascript:`).
	let isLinkAllowed = $derived(!safety?.untrusted || isSafeMarkdownUrl(props.token.href));

	let shouldOpenInNewTab = $derived(
		!href.startsWith(domain) &&
			(href.startsWith('http') || href.startsWith('/privacy') || href.includes('/faq'))
	);
</script>

<!--
	`break-words` keeps bare URLs from overflowing the viewport: a pasted link is
	one unbreakable word, so without it a long href forces the whole page wider
	than the screen on mobile.
-->
<Txt
	component="span"
	variant={props.variant}
	class={twMerge('inline w-fit break-words', props.class)}
>
	{#if isLinkAllowed}
		<Link
			{href}
			class="break-words underline has-[span]:no-underline [&>span]:underline"
			{...shouldOpenInNewTab && {
				target: '_blank',
				rel: 'noopener norefferer'
			}}
		>
			<MarkdownInlineTokens tokens={props.token.tokens} {options} {renderers} />
		</Link>
	{:else}
		<MarkdownInlineTokens tokens={props.token.tokens} {options} {renderers} />
	{/if}
</Txt>
